<template>
  <div class="legal-page">
    <header class="legal-topbar">
      <router-link to="/" class="legal-back-link">
        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
          <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        {{ $t('legal.backToApp_23fdde0') }}
      </router-link>
      <span class="legal-topbar-brand">Sparrow</span>
    </header>

    <div class="legal-shell">
      <aside class="legal-toc-desktop" aria-label="Table of contents">
        <p class="legal-toc-heading">{{ $t('legal.onThisPage_993081e') }}</p>
        <ul>
          <li v-for="s in sections" :key="s.id">
            <a :href="pageHashBase + '#' + s.id" @click="scrollTo(s.id, $event)">{{ s.label }}</a>
          </li>
        </ul>
      </aside>

      <main class="legal-content">
        <details class="legal-toc-mobile">
          <summary>{{ $t('legal.onThisPage_993081e') }}</summary>
          <ul>
            <li v-for="s in sections" :key="'m-' + s.id">
              <a :href="pageHashBase + '#' + s.id" @click="scrollTo(s.id, $event)">{{ s.label }}</a>
            </li>
          </ul>
        </details>

        <div class="legal-hero">
          <p class="legal-eyebrow">Sparrow</p>
          <h1>{{ $t('pages.PrivacyPolicy.title_e71f479') }}</h1>
          <p class="legal-subtitle">{{ $t('pages.PrivacyPolicy.subtitle_ec63f07') }}</p>
          <div class="legal-meta">
            <span><strong>Version 1.0</strong> &middot; Effective 24 July 2026</span>
            <span>Applies to: sparrownests.com, app.sparrownests.com and related Sparrow services.</span>
          </div>
        </div>

        <section id="section-1">
          <h2>1. About this Policy</h2>
          <p>This Privacy Policy explains how Sparrow (&ldquo;Sparrow&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo; or &ldquo;our&rdquo;) collects, holds, processes, uses, transfers and protects personal data through sparrownests.com, app.sparrownests.com and related services (collectively, the &ldquo;Platform&rdquo;).</p>
          <p>This Policy is intended to be read together with Sparrow&rsquo;s Terms of Use and any short-form Personal Information Collection Statement shown at the point where information is collected.</p>
          <p>Sparrow is committed to handling personal data in accordance with the Personal Data (Privacy) Ordinance (Cap. 486) of Hong Kong (&ldquo;PDPO&rdquo;) and the applicable Data Protection Principles.</p>
        </section>

        <section id="section-2">
          <h2>2. Key Definitions</h2>
          <div class="legal-table-wrap">
            <table class="legal-table">
              <thead>
                <tr><th>Term</th><th>Meaning</th></tr>
              </thead>
              <tbody>
                <tr>
                  <td>Personal data</td>
                  <td>Recorded information relating directly or indirectly to a living individual from which it is practicable for the individual&rsquo;s identity to be directly or indirectly ascertained.</td>
                </tr>
                <tr>
                  <td>Verified student account</td>
                  <td>An account for which Sparrow has confirmed access to an eligible university email address. It does not by itself confirm occupancy of a specific property or room.</td>
                </tr>
                <tr>
                  <td>Review content</td>
                  <td>Ratings, written comments, room and property details, stay information, photographs and other material submitted to Sparrow.</td>
                </tr>
                <tr>
                  <td>Housing provider</td>
                  <td>A university, student residence operator, company, agency, property manager or private landlord responsible for accommodation.</td>
                </tr>
                <tr>
                  <td>Operator report</td>
                  <td>A filtered, redacted, paraphrased, room-specific, benchmarked or aggregated report provided to a relevant housing provider.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section id="section-3">
          <h2>3. Personal Data We Collect</h2>
          <h3>3.1 Account and verification data</h3>
          <ul>
            <li>University email address, university affiliation and verification status.</li>
            <li>Name or account information supplied during registration, even where the name is not displayed publicly.</li>
            <li>Authentication records, account identifiers, login activity and account-security information.</li>
            <li>Communications with Sparrow, support requests, complaints and correction requests.</li>
          </ul>
          <h3>3.2 Review and housing-experience data</h3>
          <ul>
            <li>Property name, address, exact building location, floor, room identifier and room type.</li>
            <li>Ratings, written reviews, dates or approximate periods of stay and answers to review questions.</li>
            <li>Photographs, captions and technical metadata associated with submitted files.</li>
            <li>Evidence voluntarily provided for moderation, verification or dispute handling. Such evidence will not normally be displayed to other users.</li>
          </ul>
          <h3>3.3 Technical and usage data</h3>
          <ul>
            <li>IP address, device identifiers, browser type, operating system, language settings, pages viewed and timestamps.</li>
            <li>Cookie, session, analytics, security and fraud-prevention information.</li>
            <li>Approximate location or precise device location only where a user enables a location feature and grants permission.</li>
          </ul>
        </section>

        <section id="section-4">
          <h2>4. How We Collect Personal Data</h2>
          <ul>
            <li>Directly from users when they register, verify an account, submit a review, upload photographs, contact Sparrow or exercise privacy rights.</li>
            <li>Automatically through cookies, logs, security tools and analytics technologies when users access the Platform.</li>
            <li>From reliable public or official sources to confirm basic property, university, company or operator details.</li>
            <li>From housing providers or other users when they submit corrections, verification material or complaints.</li>
          </ul>
        </section>

        <section id="section-5">
          <h2>5. Purposes for Which We Use Personal Data</h2>
          <ul>
            <li>To create, authenticate and secure student accounts and confirm university-email eligibility.</li>
            <li>To provide student-only access to restricted review content and prevent access by ineligible or abusive accounts.</li>
            <li>To publish room- and property-level information, ratings, photographs and pseudonymous reviews.</li>
            <li>To verify or label property details as &ldquo;Property details verified&rdquo; or &ldquo;Property details unverified&rdquo;.</li>
            <li>To moderate content, investigate complaints, correct inaccuracies and enforce the Terms of Use.</li>
            <li>To detect credential sharing, scraping, bulk access, fraud, security incidents and other misuse.</li>
            <li>To prepare filtered, redacted, paraphrased, room-specific, benchmarked or aggregated insights for housing providers.</li>
            <li>To operate, maintain, analyse and improve the Platform and develop new features.</li>
            <li>To communicate with users about accounts, service updates, moderation and support.</li>
            <li>To comply with legal obligations, protect rights and safety, and respond to lawful requests.</li>
          </ul>
        </section>

        <section id="section-6">
          <h2>6. What Other Users Can See</h2>
          <p>Reviewers are displayed pseudonymously, normally as &ldquo;A student from [verified university]&rdquo;. Sparrow may instead display &ldquo;Verified student&rdquo; where showing the university could create an unnecessary identification risk.</p>
          <p>Other authenticated student users may see the review content permitted by Sparrow, including property and room identifiers, ratings, written comments and approved photographs. They will not be shown the reviewer&rsquo;s name, email address or private account information.</p>
          <p>Pseudonymity is not the same as guaranteed anonymity. A housing provider, former roommate, neighbour or other person familiar with the circumstances may sometimes infer the reviewer&rsquo;s identity from the room, timing, photographs or events described.</p>
        </section>

        <section id="section-7">
          <h2>7. Housing Provider and Operator Reports</h2>
          <p>Sparrow may provide a relevant housing provider with filtered information derived from reviews concerning its accommodation and comparable nearby accommodation. Reports may include room-specific findings, nearby benchmarks, issue frequencies, trends, scores and improvement recommendations.</p>
          <p>Sparrow will not ordinarily provide housing providers with student names, university email addresses, account identifiers, raw account records or unrestricted access to raw reviews. Where appropriate, Sparrow may include redacted or selected photographs or excerpts after removing personal or unnecessary identifying information.</p>
          <p>Because a report may concern a specific room, Sparrow cannot guarantee that the recipient will be unable to infer who submitted the underlying information. Users are notified of this possibility before submitting a review.</p>
        </section>

        <section id="section-8">
          <h2>8. Organisation Names, Property Locations and Room Details</h2>
          <p>Sparrow may display the names of universities, incorporated operators, residence companies and agencies where Sparrow reasonably believes they are connected to the property. Sparrow generally does not display the names of individual private landlords, individual agents, tenants or employees unless there is a specific lawful and justified reason to do so.</p>
          <p>The Platform may display exact building addresses, building-level map pins, floors and room identifiers so that experiences can be compared at room level. Sparrow may approximate, restrict or remove location information where necessary for privacy, safety, accuracy or legal reasons.</p>
          <p>&ldquo;Property details verified&rdquo; means Sparrow has checked selected basic information such as the property identity, location or management relationship. It does not mean Sparrow physically inspected, certified, approved or endorsed the property, its safety or its present condition.</p>
        </section>

        <section id="section-9">
          <h2>9. Photographs and Other Uploaded Content</h2>
          <p>Users must submit only photographs they took themselves or are authorised to share. Photographs must not intentionally show identifiable people, personal documents, names, contact details, access codes, keys, security arrangements or other private information.</p>
          <p>Sparrow may crop, resize, blur, redact, remove metadata from, decline or remove uploaded material for privacy, safety, moderation or technical reasons. Sparrow does not guarantee that automated or manual moderation will detect every sensitive detail, and users remain responsible for reviewing files before submission.</p>
        </section>

        <section id="section-10">
          <h2>10. Disclosure and Transfer of Personal Data</h2>
          <p>Sparrow may disclose or transfer personal data to the following classes of recipients only where reasonably necessary for the stated purposes:</p>
          <ul>
            <li>Authenticated student users, limited to pseudonymous review and property content permitted by the Platform.</li>
            <li>Relevant housing providers, limited to filtered, redacted, paraphrased, room-specific or aggregated insights as described in this Policy.</li>
            <li>Hosting, database, authentication, analytics, security, communications, storage, mapping and other service providers acting on Sparrow&rsquo;s behalf.</li>
            <li>Professional advisers, insurers, auditors, prospective investors or business partners subject to appropriate confidentiality protections.</li>
            <li>Law-enforcement bodies, regulators, courts or other persons where required or permitted by law or necessary to protect rights and safety.</li>
            <li>A successor or purchaser in connection with a merger, financing, restructuring or transfer of all or part of Sparrow, subject to appropriate safeguards.</li>
          </ul>
          <p>Sparrow does not sell personal data to advertisers. Sparrow will not provide personal data to third parties for their own direct marketing without the user&rsquo;s prescribed consent or indication of no objection where required by law.</p>
        </section>

        <section id="section-11">
          <h2>11. Service Providers and Cross-Border Processing</h2>
          <p>Sparrow may use service providers whose systems or personnel are located outside Hong Kong. Personal data may therefore be stored or processed in other jurisdictions. Sparrow will take reasonably practicable steps to select appropriate providers and require suitable confidentiality, security and data-protection measures.</p>
        </section>

        <section id="section-12">
          <h2>12. Cookies and Analytics</h2>
          <p>Sparrow may use essential cookies and similar technologies for login, account security, preferences and Platform operation. Sparrow may also use analytics technologies to understand usage, identify errors and improve the Platform. Users may control non-essential cookies through available preference tools or browser settings, although disabling certain technologies may affect functionality.</p>
        </section>

        <section id="section-13">
          <h2>13. Data Accuracy, Retention and Deletion</h2>
          <p>Sparrow takes reasonably practicable steps to keep personal data accurate for the purposes for which it is used. Users should update their account information and use the correction process where property or review information is inaccurate.</p>
          <p>Sparrow retains personal data only for as long as reasonably necessary for the purposes described in this Policy, including account operation, review integrity, security, complaint handling and legal compliance. Retention periods may vary according to the type of data and the reason it is held.</p>
          <p>When an account is deleted, Sparrow may delete or anonymise account identifiers. Review content may be deleted, withdrawn or retained in genuinely de-identified form, subject to applicable law, the user&rsquo;s request, moderation records and the need to maintain the integrity of the Platform.</p>
        </section>

        <section id="section-14">
          <h2>14. Security</h2>
          <p>Sparrow uses reasonably practicable administrative, technical and organisational safeguards appropriate to the nature of the data, which may include access controls, encrypted transmission, authentication, monitoring, backups, restricted staff access and service-provider controls.</p>
          <p>No online service can guarantee absolute security. Users must protect their credentials, must not share student accounts and should promptly report suspected unauthorised access. Sparrow will assess and respond to suspected data incidents in accordance with applicable law and its internal procedures.</p>
        </section>

        <section id="section-15">
          <h2>15. Direct Marketing and Communications</h2>
          <p>Sparrow may send service communications necessary to operate an account. Marketing communications will be sent only in accordance with applicable requirements and available consent or preference settings. Users may opt out of marketing at any time without charge, although they may continue to receive essential account or service notices.</p>
        </section>

        <section id="section-16">
          <h2>16. Data Access and Correction Rights</h2>
          <p>Subject to the PDPO, an individual may request access to personal data held about them and request correction of inaccurate personal data. Sparrow may require reasonable information to verify the requester&rsquo;s identity and authority. Any fee charged for a data access request will not be excessive.</p>
          <p>Requests should be addressed to Sparrow&rsquo;s Privacy Officer. You can reach the Privacy Officer by contacting us through the Sparrow Platform.</p>
        </section>

        <section id="section-17">
          <h2>17. Young Users</h2>
          <p>The Platform is intended primarily for university students aged 18 or older. A younger university student should use the Platform only where permitted by law and with the consent of a parent or legal guardian where required. Sparrow may request confirmation of age or consent and may restrict or remove an account where appropriate.</p>
        </section>

        <section id="section-18">
          <h2>18. Changes to this Policy</h2>
          <p>Sparrow may update this Policy to reflect changes to the Platform, its practices or applicable requirements. The updated version will state its effective date. Sparrow will provide additional notice where a change materially affects how personal data is used.</p>
        </section>

        <section id="section-19">
          <h2>19. Contact</h2>
          <p>Questions, complaints, data access requests and correction requests may be sent to Sparrow. Please contact us through the Sparrow Platform.</p>
        </section>

        <section id="appendix-a" class="legal-appendix">
          <span class="legal-appendix-label">Appendix A</span>
          <h2>Short-form Personal Information Collection Statement</h2>
          <p>Recommended for sign-up and review-submission screens. Linked to the full Privacy Policy above.</p>
          <p>Sparrow collects your university email, university affiliation, account and technical information to verify eligibility, create and secure your account, provide student-only access, prevent misuse and communicate with you. Required fields are obligatory for registration; without them, Sparrow cannot create or verify your account. Your reviewer display name will normally be &ldquo;A student from [verified university]&rdquo; or &ldquo;Verified student&rdquo;, not your personal name. Personal data may be handled by Sparrow&rsquo;s hosting, authentication, analytics, security and communications providers. You may request access to or correction of personal data by contacting Sparrow&rsquo;s Privacy Officer through the Sparrow Platform.</p>
          <p>Submitting a review is voluntary. Sparrow will use your review, room and property information, ratings, photographs and related records to publish student-only housing experiences, moderate the Platform, verify property details, improve Sparrow and prepare filtered, redacted, paraphrased, room-specific or aggregated reports for relevant housing providers. Sparrow will not ordinarily disclose your name, university email or private account details to a housing provider, but a person familiar with the room or circumstances may sometimes infer your identity. Review content may be visible to authenticated student users. For access or correction requests, contact Sparrow&rsquo;s Privacy Officer through the Sparrow Platform.</p>
        </section>

        <p class="legal-footer">
          See also Sparrow&rsquo;s <router-link to="/terms-of-use">Terms of Use</router-link>.
          &middot;
          <router-link to="/">{{ $t('legal.backToApp_23fdde0') }}</router-link>
        </p>
      </main>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PrivacyPolicy',
  data() {
    return {
      sections: [
        { id: 'section-1', label: '1. About this Policy' },
        { id: 'section-2', label: '2. Key Definitions' },
        { id: 'section-3', label: '3. Personal Data We Collect' },
        { id: 'section-4', label: '4. How We Collect Personal Data' },
        { id: 'section-5', label: '5. Purposes for Which We Use Personal Data' },
        { id: 'section-6', label: '6. What Other Users Can See' },
        { id: 'section-7', label: '7. Housing Provider and Operator Reports' },
        { id: 'section-8', label: '8. Organisation Names, Property Locations and Room Details' },
        { id: 'section-9', label: '9. Photographs and Other Uploaded Content' },
        { id: 'section-10', label: '10. Disclosure and Transfer of Personal Data' },
        { id: 'section-11', label: '11. Service Providers and Cross-Border Processing' },
        { id: 'section-12', label: '12. Cookies and Analytics' },
        { id: 'section-13', label: '13. Data Accuracy, Retention and Deletion' },
        { id: 'section-14', label: '14. Security' },
        { id: 'section-15', label: '15. Direct Marketing and Communications' },
        { id: 'section-16', label: '16. Data Access and Correction Rights' },
        { id: 'section-17', label: '17. Young Users' },
        { id: 'section-18', label: '18. Changes to this Policy' },
        { id: 'section-19', label: '19. Contact' },
        { id: 'appendix-a', label: 'Appendix A · Personal Information Collection Statement' },
      ],
    };
  },
  computed: {
    // This app uses vue-router in hash mode (URLs look like /#/privacy-policy).
    // Anchor hrefs are built as "#<route-path>#<section-id>" so that a full
    // page load, copy-pasted link or middle-click still resolves to this
    // route (vue-router parses the "#section-id" suffix as route.hash),
    // instead of clobbering the router's own hash and landing nowhere.
    pageHashBase() {
      return '#' + (this.$route ? this.$route.path : '/privacy-policy');
    },
  },
  methods: {
    scrollTo(id, evt) {
      if (evt) evt.preventDefault();
      const el = document.getElementById(id);
      if (el && el.scrollIntoView) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      // Use replaceState (not router.push/location.hash) so the URL reflects
      // the section without firing a hashchange/popstate event: the app's
      // global router.scrollBehavior() always resets scroll to the top on
      // every navigation, which would otherwise undo the smooth scroll above.
      if (window.history && window.history.replaceState) {
        window.history.replaceState(null, '', this.pageHashBase + '#' + id);
      }
      const details = this.$el.querySelector('.legal-toc-mobile');
      if (details) details.open = false;
    },
  },
  mounted() {
    // Support opening the page with a section already in the URL
    // (e.g. shared link built from this page's own TOC). Deferred with a
    // short delay because the app's global router.scrollBehavior() forces
    // the initial navigation to scroll to the top; this needs to run after
    // that resolves, not before.
    const hash = this.$route && this.$route.hash;
    if (hash) {
      const id = hash.replace(/^#/, '');
      this.$nextTick(() => {
        setTimeout(() => this.scrollTo(id), 150);
      });
    }
  },
};
</script>

<style>
@import './css/legal.css';
</style>
